/**
 * StatusLed — read-only indicator lamp. Reads `midi/inbound.ts` and lights
 * when a matching event's value crosses the LED's threshold. That store is
 * LIVE as of 2026-08-27 (`midi/inboundFeed.ts` feeds it), so this lights for
 * the first time since `packages/bridge/` was deleted 2026-08-20.
 *
 * ⚠️ `item.channel` is 0-based ("MIDI channel 0-15", `projectStore.ts`) and
 * the incoming event's channel is the raw 0-based nibble, so the comparison
 * below is a direct match with NO conversion. Do not add one — some other
 * item kinds in this project store 1-based channels, and "fixing" this to
 * agree with them would break every binding that currently matches.
 *
 * Never sends. No pointer handlers, no fire path. In edit mode the
 * EditableItem wrapper still owns click-to-select + drag; in test mode
 * pointer events pass through (the LED is a visual, not an affordance).
 *
 * Matching rules:
 *   sourceType='midi.cc'  → msg.kind='cc'     && msg.number=ccNumber
 *   sourceType='midi.note' → msg.kind='noteon' && msg.number=note
 *                            (noteoff or noteon vel=0 turns OFF)
 * Plus (channel === channel) in every case.
 *
 * ON: msg.value >= threshold. OFF: msg.value < threshold.
 *
 * We render the ON state color at full saturation; OFF state uses a dim
 * outline of the same palette token so the LED still reads as "the
 * green light" while dark — never blank space.
 */

import { createSignal, createEffect, type Accessor } from 'solid-js';
import { midiInLast } from '../midi/inbound.ts';
import type { StatusLedItem, LayoutColor } from '../layout/store.ts';

interface StatusLedProps {
  item: StatusLedItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

export function StatusLed(props: StatusLedProps) {
  const [on, setOn] = createSignal(false);

  createEffect(() => {
    const msg = midiInLast();
    if (!msg) return;
    const src = props.item.sourceType ?? 'midi.cc';
    if (msg.channel !== props.item.channel) return;
    const threshold = props.item.threshold ?? 1;
    if (src === 'midi.cc') {
      if (msg.kind !== 'cc') return;
      if (msg.number !== (props.item.ccNumber ?? 0)) return;
      setOn(msg.value >= threshold);
      return;
    }
    // midi.note
    if (msg.kind !== 'noteon' && msg.kind !== 'noteoff') return;
    if (msg.number !== (props.item.note ?? 60)) return;
    // noteon with velocity >= threshold = ON. noteoff or vel=0 = OFF.
    const value = msg.kind === 'noteon' ? msg.value : 0;
    setOn(value >= threshold);
  });

  const name = () => props.item.name ?? '';
  const showName = () => !props.item.hideName;
  const shape = () => props.item.shape ?? 'circle';

  return (
    <div
      class="status-led"
      data-on={on() ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape={shape()}
    >
      <div class="status-led-lamp" aria-hidden="true" />
      {showName() && name() && (
        <span class="status-led-name">{name()}</span>
      )}
    </div>
  );
}
